import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { ArrowForward, ArrowBack, LastPage } from "@mui/icons-material";
import { format, parseISO } from "date-fns";
import { FormattedMessage, useIntl } from "react-intl";

import { humanReadableDateTimeFormat } from "@dzambalaorg/constants";
import { useApiCall } from "@dzambalaorg/react-hooks";
import { ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import type { IContract, ILotteryRound } from "@framework/types";
import { Button, Tooltip, Typography } from "@dzambalaorg/ui-kit";

import { emptyLotteryRound } from "../../../../../../components/common/interfaces";
import { Details } from "./details";
import {
  Root,
  StyledRoundPagination,
  StyledTime,
  StyledPaper,
  StyledRound,
  StyledRoundDescription,
  StyledRoundWrapper,
  StyledWrapper,
} from "./styled";

interface ILotteryStatisticProps {
  contract: IContract;
}

export const LotteryStatistic = (props: ILotteryStatisticProps) => {
  const { contract } = props;

  const { formatMessage } = useIntl();

  const [round, setRound] = useState<ILotteryRound | null>(emptyLotteryRound);
  const [roundIds, setRoundIds] = useState<number[]>([]);
  const [selectedRoundId, setSelectedRoundId] = useState<number | null>(null);
  const [firstRoundId, setFirstRoundId] = useState<number | null>(null);
  const [lastRoundId, setLastRoundId] = useState<number | null>(null);

  const { fn: getLatestRound, isLoading: isLatestRoundLoading } = useApiCall(
    async (api, roundId: number) => {
      return api.fetchJson({
        url: `/lottery/rounds/${roundId}`,
        data: {
          contractId: contract.id,
        },
      });
    },
    { success: false, error: false },
  );

  const fetchRound = async (roundId: number): Promise<any> => {
    return getLatestRound(void 0, roundId)
      .then((json: ILotteryRound) => {
        setRound(json);
      })
      .catch(e => {
        console.error(e);
      });
  };

  const { fn: getAllRoundIds, isLoading: isAllRoundIdsLoading } = useApiCall(
    async api => {
      return api.fetchJson({
        url: "/lottery/rounds/all",
        data: {
          contractId: contract.id,
        },
      });
    },
    { success: false, error: false },
  );

  const fetchAllRoundIds = async (): Promise<any> => {
    return getAllRoundIds()
      .then((json: [Array<number>, number]) => {
        if (json && Array.isArray(json) && Array.isArray(json[0]) && json[1]) {
          const roundIds = json[0];
          const firstRoundId = roundIds[0];
          const lastRoundId = roundIds[roundIds.length - 1];
          setRoundIds(roundIds);
          setFirstRoundId(firstRoundId);
          setLastRoundId(lastRoundId);
          setSelectedRoundId(lastRoundId);
        }
      })
      .catch(e => {
        console.error(e);
      });
  };

  const handlePrevRound = () => {
    if (roundIds.length && selectedRoundId) {
      const selectedRoundIdIndex = roundIds.indexOf(selectedRoundId);
      if (selectedRoundIdIndex > 0) {
        setSelectedRoundId(roundIds[selectedRoundIdIndex - 1]);
      }
    }
  };
  const handleNextRound = () => {
    if (roundIds.length && selectedRoundId) {
      const selectedRoundIdIndex = roundIds.indexOf(selectedRoundId);
      if (selectedRoundIdIndex < roundIds.length - 1) {
        setSelectedRoundId(roundIds[selectedRoundIdIndex + 1]);
      }
    }
  };
  const handleLastRound = () => {
    if (lastRoundId) {
      setSelectedRoundId(lastRoundId);
    }
  };

  useEffect(() => {
    if (contract.id) {
      void fetchAllRoundIds();
    }
  }, [contract.id]);

  useEffect(() => {
    if (selectedRoundId) {
      void fetchRound(selectedRoundId);
    }
  }, [selectedRoundId]);

  return (
    <ProgressOverlay isLoading={isLatestRoundLoading || isAllRoundIdsLoading}>
      <Root>
        <Typography variant="h4">
          <FormattedMessage id="pages.lottery.contract.statistics.title" />
        </Typography>
        <StyledPaper>
          {roundIds.length && round?.roundId ? (
            <>
              <StyledRoundWrapper>
                <StyledRoundDescription>
                  <StyledRound>
                    <FormattedMessage
                      id="pages.lottery.contract.statistics.controls.round"
                      values={{ roundId: round.roundId }}
                    />
                  </StyledRound>
                  <StyledTime>
                    <FormattedMessage
                      id="pages.lottery.contract.statistics.controls.start"
                      values={{ datetime: format(parseISO(round.startTimestamp), humanReadableDateTimeFormat) }}
                    />
                  </StyledTime>
                  <StyledTime>
                    <FormattedMessage
                      id="pages.lottery.contract.statistics.controls.drawn"
                      values={{ datetime: format(parseISO(round.endTimestamp), humanReadableDateTimeFormat) }}
                    />
                  </StyledTime>
                </StyledRoundDescription>
                <StyledRoundPagination>
                  <Button intent="icon" disabled={selectedRoundId === firstRoundId} onClick={handlePrevRound}>
                    <Tooltip
                      arrow={false}
                      title={formatMessage({ id: "pages.lottery.contract.statistics.controls.previous" })}
                    >
                      <ArrowBack />
                    </Tooltip>
                  </Button>
                  <Button intent="icon" disabled={selectedRoundId === lastRoundId} onClick={handleNextRound}>
                    <Tooltip
                      arrow={false}
                      title={formatMessage({ id: "pages.lottery.contract.statistics.controls.next" })}
                    >
                      <ArrowForward />
                    </Tooltip>
                  </Button>
                  <Button intent="icon" disabled={selectedRoundId === lastRoundId} onClick={handleLastRound}>
                    <Tooltip
                      arrow={false}
                      title={formatMessage({ id: "pages.lottery.contract.statistics.controls.last" })}
                    >
                      <LastPage />
                    </Tooltip>
                  </Button>
                </StyledRoundPagination>
              </StyledRoundWrapper>
              <Typography variant="body2">
                <FormattedMessage id="pages.lottery.contract.statistics.winNumbers" />
              </Typography>
              <StyledWrapper>
                {new Array(36).fill(null).map((e, i) => {
                  const isSelected = round.numbers[i];

                  if (!isSelected) {
                    return null;
                  }

                  return (
                    <Button intent="icon" size="medium" key={i}>
                      {i + 1}
                    </Button>
                  );
                })}
              </StyledWrapper>
              <Details round={round} commission={contract.parameters.commission as string} />
            </>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              <Typography variant="body2" color="textSecondary">
                <FormattedMessage id="pages.lottery.contract.statistics.empty" />
              </Typography>
            </Box>
          )}
        </StyledPaper>
      </Root>
    </ProgressOverlay>
  );
};
